import React, { useState, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  textArea: {
    minHeight: "200px",
    fontFamily: "monospace",
  },
  statsBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  numberPreview: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    maxHeight: "150px",
    overflowY: "auto",
  },
}));

const normalizePhoneNumber = (phone) => {
  // Remove tudo que não é número
  return phone.replace(/\D/g, "");
};

const validatePhoneNumber = (phone) => {
  const normalized = normalizePhoneNumber(phone);
  // Aceita números de 10 a 15 dígitos (com código do país)
  return normalized.length >= 10 && normalized.length <= 15;
};

const parsePhoneNumbers = (text) => {
  if (!text || !text.trim()) return [];
  
  // Divide por linhas, vírgulas, ponto-e-vírgula ou espaços
  const separators = /[\n\r,;]+/;
  const lines = text.split(separators);
  
  const numbers = [];
  const invalid = [];
  
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    
    // Tenta extrair número da linha (pode ter nome antes)
    // Formato: "Nome 5511999999999" ou "5511999999999" ou "11 99999-9999"
    const numberMatch = trimmed.match(/(\d[\d\s\-\(\)]+)/);
    if (numberMatch) {
      const numberStr = numberMatch[1];
      if (validatePhoneNumber(numberStr)) {
        numbers.push({
          original: trimmed,
          number: normalizePhoneNumber(numberStr),
          name: trimmed.replace(numberStr, "").trim() || null,
        });
      } else {
        invalid.push(trimmed);
      }
    } else {
      // Se não encontrou número, tenta validar a linha inteira
      if (validatePhoneNumber(trimmed)) {
        numbers.push({
          original: trimmed,
          number: normalizePhoneNumber(trimmed),
          name: null,
        });
      } else {
        invalid.push(trimmed);
      }
    }
  });
  
  return { numbers, invalid };
};

const ManualAddContactsModal = ({
  open,
  onClose,
  contactListProId,
  onSuccess,
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [textInput, setTextInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [parsedData, setParsedData] = useState({ numbers: [], invalid: [] });

  const handleTextChange = (e) => {
    const value = e.target.value;
    setTextInput(value);
    if (value) {
      const parsed = parsePhoneNumbers(value);
      setParsedData(parsed);
    } else {
      setParsedData({ numbers: [], invalid: [] });
    }
  };

  const handleAddContacts = async () => {
    if (parsedData.numbers.length === 0) {
      toast.error("Nenhum número válido encontrado!");
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    let duplicateCount = 0;

    try {
      // Adiciona contatos em lote (máximo 50 por vez para não sobrecarregar)
      const batchSize = 50;
      for (let i = 0; i < parsedData.numbers.length; i += batchSize) {
        const batch = parsedData.numbers.slice(i, i + batchSize);
        
        const promises = batch.map(async (contact) => {
          try {
            await api.post(`/contact-lists-pro/${contactListProId}/items`, {
              name: contact.name,
              number: contact.number,
              optedIn: true,
            });
            successCount++;
          } catch (err) {
            if (err.response?.status === 400 && err.response?.data?.error?.includes("DUPLICATE")) {
              duplicateCount++;
            } else {
              errorCount++;
              console.error("Erro ao adicionar contato:", err);
            }
          }
        });

        await Promise.all(promises);
      }

      let message = `${successCount} contato(s) adicionado(s) com sucesso!`;
      if (duplicateCount > 0) {
        message += ` ${duplicateCount} duplicado(s) ignorado(s).`;
      }
      if (errorCount > 0) {
        message += ` ${errorCount} erro(s).`;
      }

      toast.success(message);
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Limpa o formulário
      setTextInput("");
      setParsedData({ numbers: [], invalid: [] });
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Adicionar Contatos Manualmente</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Cole os números de telefone abaixo. Você pode colar números separados por linha, vírgula ou ponto-e-vírgula.
          <br />
          Exemplo: 5511999999999 ou João 5511999999999
        </Typography>
        
        <TextField
          fullWidth
          multiline
          rows={8}
          variant="outlined"
          placeholder="Cole os números aqui...&#10;Exemplo:&#10;5511999999999&#10;5511888888888&#10;João 5511777777777"
          value={textInput}
          onChange={handleTextChange}
          className={classes.textArea}
          disabled={loading}
          margin="normal"
        />

        {textInput && (
          <Box className={classes.statsBox}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary">
                  Válidos:
                </Typography>
                <Chip
                  label={parsedData.numbers.length}
                  color="primary"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary">
                  Inválidos:
                </Typography>
                <Chip
                  label={parsedData.invalid.length}
                  color={parsedData.invalid.length > 0 ? "secondary" : "default"}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary">
                  Total:
                </Typography>
                <Chip
                  label={parsedData.numbers.length + parsedData.invalid.length}
                  size="small"
                />
              </Grid>
            </Grid>

            {parsedData.numbers.length > 0 && (
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Preview dos números válidos (máx. 20):
                </Typography>
                <Box className={classes.numberPreview}>
                  {parsedData.numbers.slice(0, 20).map((contact, idx) => (
                    <Chip
                      key={idx}
                      label={contact.name ? `${contact.name}: ${contact.number}` : contact.number}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                  {parsedData.numbers.length > 20 && (
                    <Chip
                      label={`+${parsedData.numbers.length - 20} mais...`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            )}

            {parsedData.invalid.length > 0 && parsedData.invalid.length <= 10 && (
              <Box mt={2}>
                <Typography variant="body2" color="error" gutterBottom>
                  Números inválidos:
                </Typography>
                <Box className={classes.numberPreview}>
                  {parsedData.invalid.map((inv, idx) => (
                    <Chip
                      key={idx}
                      label={inv}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleAddContacts}
          color="primary"
          variant="contained"
          disabled={loading || parsedData.numbers.length === 0}
        >
          {loading ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              Adicionando...
            </>
          ) : (
            `Adicionar ${parsedData.numbers.length} Contato(s)`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualAddContactsModal;

